import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { formatNumber, CATEGORIES, countSerialsByStatus } from '@/utils/helpers';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { exportToCSV, CSV_COLUMNS } from '@/utils/exportUtils';
import { Plus, Edit, Trash, Search } from 'lucide-react';

export const Products = () => {
  const currency = useCurrency();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin, isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    category: CATEGORIES[0],
    brand: '',
    model: '',
    sku: '',
    cost: 0,
    price: 0,
    warranty: 12,
    low_stock: 5,
    serials: []
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      // Text search
      const matchSearch = !searchTerm ||
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.serials || []).some(s => s.sn.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Category filter
      const matchCategory = !categoryFilter || p.category === categoryFilter;
      
      // Stock filter
      const inStockCount = countSerialsByStatus(p.serials, 'In Stock');
      let matchStock = true;
      if (stockFilter === 'in_stock') matchStock = inStockCount > 0;
      else if (stockFilter === 'low_stock') matchStock = inStockCount > 0 && inStockCount <= (p.low_stock || 5);
      else if (stockFilter === 'out_of_stock') matchStock = inStockCount === 0;
      
      return matchSearch && matchCategory && matchStock;
    });
  }, [searchTerm, categoryFilter, stockFilter, products]);

  const loadProducts = async () => {
    try {
      const res = await api.getProducts();
      setProducts(res.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    exportToCSV(filteredProducts, 'products', CSV_COLUMNS.products);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStockFilter('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.updateProduct(editingProduct.id, formData);
      } else {
        await api.createProduct(formData);
      }
      loadProducts();
      closeModal();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await api.deleteProduct(id);
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData(product);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: CATEGORIES[0],
        brand: '',
        model: '',
        sku: '',
        cost: 0,
        price: 0,
        warranty: 12,
        low_stock: 5,
        serials: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Products</h1>
        <Button onClick={() => openModal()} data-testid="add-product-button">
          <Plus size={20} className="mr-2" />
          Add Product
        </Button>
      </div>

      <Card>
        <FilterBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by name, brand, model, SKU, or serial..."
          filters={[
            {
              key: 'category',
              label: 'All Categories',
              value: categoryFilter,
              options: CATEGORIES,
              onChange: setCategoryFilter
            },
            {
              key: 'stock',
              label: 'All Stock',
              value: stockFilter,
              options: [
                { value: 'in_stock', label: 'In Stock' },
                { value: 'low_stock', label: 'Low Stock' },
                { value: 'out_of_stock', label: 'Out of Stock' }
              ],
              onChange: setStockFilter
            }
          ]}
          onClear={handleClearFilters}
          onExport={handleExport}
          testId="products-filter"
        />

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>In Stock</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No products found
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => {
                const inStock = countSerialsByStatus(product.serials, 'In Stock');
                return (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell>{product.category}</TableCell>
                    <TableCell>{product.brand}</TableCell>
                    <TableCell>
                      <Badge variant={inStock === 0 ? 'danger' : inStock <= product.low_stock ? 'warning' : 'success'}>
                        {inStock}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatCurrency(product.price, currency)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openModal(product)}>
                          <Edit size={16} />
                        </Button>
                        {isSuperAdmin() && (
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(product.id)}>
                            <Trash size={16} className="text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingProduct ? 'Edit Product' : 'Add Product'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Product Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </Select>

            <Input
              label="Brand"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
            />
            <Input
              label="SKU (optional)"
              value={formData.sku || ''}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              placeholder="e.g. RTX4060-MSI-VENT"
              data-testid="product-sku"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Cost"
              type="number"
              step="0.01"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
              required
            />

            <Input
              label="Price"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Warranty (months)"
              type="number"
              value={formData.warranty}
              onChange={(e) => setFormData({ ...formData, warranty: parseInt(e.target.value) })}
            />

            <Input
              label="Low Stock Alert"
              type="number"
              value={formData.low_stock}
              onChange={(e) => setFormData({ ...formData, low_stock: parseInt(e.target.value) })}
            />
          </div>

          {/* Serial Number Management */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Serial Numbers ({formData.serials?.length || 0})
              </label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFormData({
                    ...formData,
                    serials: [...(formData.serials || []), { sn: '', status: 'In Stock' }]
                  });
                }}
                data-testid="add-serial-button"
              >
                + Add Serial
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(formData.serials || []).map((serial, idx) => (
                <div key={idx} className="flex gap-2 items-center" data-testid={`serial-row-${idx}`}>
                  <Input
                    placeholder="Serial Number"
                    value={serial.sn}
                    onChange={(e) => {
                      const newSerials = [...formData.serials];
                      newSerials[idx] = { ...newSerials[idx], sn: e.target.value };
                      setFormData({ ...formData, serials: newSerials });
                    }}
                    className="flex-1"
                    data-testid={`serial-input-${idx}`}
                  />
                  <Select
                    value={serial.status}
                    onChange={(e) => {
                      const newSerials = [...formData.serials];
                      newSerials[idx] = { ...newSerials[idx], status: e.target.value };
                      setFormData({ ...formData, serials: newSerials });
                    }}
                    className="w-40"
                  >
                    <option value="In Stock">In Stock</option>
                    <option value="Sold">Sold</option>
                    <option value="RMA">RMA</option>
                    <option value="Loaned">Loaned</option>
                    <option value="Defective">Defective</option>
                    <option value="Returned">Returned</option>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        serials: formData.serials.filter((_, i) => i !== idx)
                      });
                    }}
                    data-testid={`remove-serial-${idx}`}
                  >
                    <span className="text-red-600">×</span>
                  </Button>
                </div>
              ))}
              {(!formData.serials || formData.serials.length === 0) && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                  No serials added. Click "+ Add Serial" to track inventory by serial number.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
